import { useState } from "react";
import { IconPackage, IconAlertTriangle, IconChevronDown, IconChevronUp, IconCheck, IconArrowRight } from "@tabler/icons-react";
import type { ImportError } from "../pages/my-product-bulk-upload-page";
import { Heading, Text } from "../../../../components/common/typography";
import { Chip } from "../../../../components/common/chip";
import { Button } from "../../../../components/common/button";

export interface PreviewProduct {
  id?: string;
  name: string;
  image?: string | null;
  status?: "draft" | "submitted";
  variants?: Array<{
    sku?: string | null;
    image?: string | null;
    pricingTiers?: Array<{ price?: number; minQty?: number }>;
  }>;
}

interface Props {
  requests: PreviewProduct[];
  errors: ImportError[];
  mode?: "preview" | "confirmed";
  onProductClick?: (product: PreviewProduct) => void;
  onProductSubmit?: (product: PreviewProduct) => void;
  submittingId?: string | null;
}

function priceRange(product: PreviewProduct): { min: number; max: number } | null {
  const prices: number[] = [];
  for (const v of product.variants ?? []) {
    for (const t of v.pricingTiers ?? []) {
      if (t.price != null && t.price > 0) prices.push(t.price);
    }
  }
  if (prices.length === 0) return null;
  return { min: Math.min(...prices), max: Math.max(...prices) };
}

export function BulkImportPreview({
  requests,
  errors,
  mode = "confirmed",
  onProductClick,
  onProductSubmit,
  submittingId,
}: Props) {
  const [expandedProduct, setExpandedProduct] = useState<string | null>(null);

  const errorsByProduct = new Map<string, ImportError[]>();
  for (const err of errors) {
    const key = err.product;
    if (!errorsByProduct.has(key)) errorsByProduct.set(key, []);
    errorsByProduct.get(key)!.push(err);
  }

  const isPreview = mode === "preview";
  const draftCount = requests.filter((r) => r.status !== "submitted").length;

  return (
    <div className="flex flex-col gap-6">
      {requests.length > 0 && (
        <div className="rounded-2xl border border-borderDefault bg-bgWhite p-6 shadow-[0px_2px_12px_0px_rgba(190,189,217,0.2)]">
          <Heading variant="h3" tone="dark" className="mb-1">
            {isPreview
              ? `${requests.length} product${requests.length !== 1 ? "s" : ""} ready to import`
              : `${requests.length} product${requests.length !== 1 ? "s" : ""} imported`}
          </Heading>
          <Text variant="bodySm" tone="muted" className="mb-5">
            {isPreview
              ? "Click a product to review details. When ready, confirm to create drafts."
              : draftCount > 0
                ? `${draftCount} draft${draftCount !== 1 ? "s" : ""} remaining. Click a product to review or submit individually.`
                : "All products have been submitted for review."}
          </Text>

          <div className="flex flex-col gap-3">
            {requests.map((req, idx) => {
              const variantCount = req.variants?.length ?? 0;
              const range = priceRange(req);
              const imgSrc = req.image ?? req.variants?.[0]?.image;
              const isSubmitted = req.status === "submitted";
              const canClick = onProductClick != null;

              const tile = (
                <div className="flex items-center gap-4">
                  {imgSrc ? (
                    <img
                      src={imgSrc}
                      alt={req.name ?? ""}
                      className="size-12 shrink-0 rounded-xl object-cover"
                    />
                  ) : (
                    <div className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-bgMuted">
                      <IconPackage size={22} className="text-textMuted" />
                    </div>
                  )}

                  <div className="flex flex-1 flex-col gap-0.5 min-w-0">
                    <div className="flex items-center gap-2">
                      <Text variant="bodyBold" tone="dark" className="line-clamp-1">
                        {req.name || "Untitled product"}
                      </Text>
                      {isSubmitted && <Chip variant="approved">Submitted</Chip>}
                      {!isPreview && !isSubmitted && <Chip variant="tertiary">Draft</Chip>}
                    </div>
                    <div className="flex items-center gap-2">
                      <Text variant="bodySm" tone="muted">
                        {variantCount} variant{variantCount !== 1 ? "s" : ""}
                      </Text>
                      {range && (
                        <>
                          <Text variant="bodySm" tone="muted">·</Text>
                          <Text variant="bodySm" tone="muted">
                            {range.min === range.max
                              ? `QAR ${range.min}`
                              : `QAR ${range.min} – ${range.max}`}
                          </Text>
                        </>
                      )}
                    </div>
                  </div>

                  {!isPreview && req.id && !isSubmitted && onProductSubmit && (
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={submittingId === req.id}
                      leftIcon={<IconCheck size={14} />}
                      onClick={(e) => {
                        e.stopPropagation();
                        onProductSubmit(req);
                      }}
                    >
                      {submittingId === req.id ? "Submitting…" : "Submit"}
                    </Button>
                  )}

                  {canClick && (
                    <IconArrowRight size={18} className="shrink-0 text-textMuted" />
                  )}
                </div>
              );

              return canClick ? (
                <button
                  key={req.id ?? `preview-${idx}`}
                  type="button"
                  className={[
                    "w-full rounded-xl border px-5 py-4 text-left transition-colors",
                    isSubmitted
                      ? "border-accentGreenTint bg-accentGreenTintBg/30"
                      : "border-borderDefault bg-bgLight hover:border-borderDark hover:bg-bgMuted/40",
                  ].join(" ")}
                  onClick={() => onProductClick(req)}
                >
                  {tile}
                </button>
              ) : (
                <div
                  key={`preview-${idx}`}
                  className="w-full rounded-xl border border-borderDefault bg-bgLight px-5 py-4"
                >
                  {tile}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {errors.length > 0 && (
        <div className="rounded-2xl border border-red-200 bg-bgWhite p-6 shadow-[0px_2px_12px_0px_rgba(190,189,217,0.2)]">
          <div className="mb-1 flex items-center gap-2">
            <IconAlertTriangle size={20} className="text-red-500" />
            <Heading variant="h3" tone="dark">
              {errors.length} variant{errors.length !== 1 ? "s" : ""} failed
            </Heading>
          </div>
          <Text variant="bodySm" tone="muted" className="mb-5">
            These rows could not be imported. Fix the issues in your spreadsheet and re-upload, or
            ask an admin to add the missing catalog values.
          </Text>

          <div className="flex flex-col gap-2">
            {[...errorsByProduct.entries()].map(([productName, productErrors]) => {
              const isExpanded = expandedProduct === productName;

              return (
                <div key={productName} className="rounded-xl border border-red-100 bg-red-50/30">
                  <button
                    type="button"
                    className="flex w-full items-center gap-3 px-5 py-3 text-left"
                    onClick={() => setExpandedProduct(isExpanded ? null : productName)}
                  >
                    {productErrors[0]?.image ? (
                      <img
                        src={productErrors[0].image}
                        alt={productName}
                        className="size-8 shrink-0 rounded-lg object-cover"
                      />
                    ) : (
                      <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-red-100">
                        <IconAlertTriangle size={16} className="text-red-500" />
                      </div>
                    )}
                    <div className="flex flex-1 flex-col gap-0.5 min-w-0">
                      <Text variant="bodyBold" tone="dark">{productName}</Text>
                      <Text variant="bodySm" tone="muted">
                        {productErrors.length} failed variant{productErrors.length !== 1 ? "s" : ""}
                      </Text>
                    </div>
                    {isExpanded
                      ? <IconChevronUp size={18} className="text-textMuted" />
                      : <IconChevronDown size={18} className="text-textMuted" />
                    }
                  </button>

                  {isExpanded && (
                    <div className="border-t border-red-100 px-5 py-3">
                      <div className="flex flex-col gap-2">
                        {productErrors.map((err, i) => (
                          <div key={i} className="flex items-start gap-3 rounded-lg bg-bgWhite px-4 py-2.5 border border-red-100">
                            <div className="flex flex-col gap-0.5 min-w-0 flex-1">
                              <div className="flex items-center gap-3">
                                {err.row != null && (
                                  <Text variant="capsBig" tone="muted">Row {err.row}</Text>
                                )}
                                {err.sku && (
                                  <Text variant="bodySm" tone="dark">SKU: {err.sku}</Text>
                                )}
                              </div>
                              <Text variant="bodySm" className="text-red-600">{err.message}</Text>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
